import React, { useState } from 'react'
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  Paper,
  useTheme,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { alpha } from '@mui/material/styles'
import { ConfigSectionProps } from '../types/EnvironmentConfig.types'

/**
 * ConfigSection Component
 * A collapsible section wrapper for grouping related configuration settings
 */
const ConfigSection: React.FC<ConfigSectionProps> = ({
  title,
  description,
  icon,
  children,
  collapsible = true,
  defaultExpanded = true,
}) => {
  const theme = useTheme()
  const [expanded, setExpanded] = useState(defaultExpanded)
  const primary = theme.palette.primary.main
  const primarySoft = alpha(primary, 0.06)
  const primaryBorder = alpha(primary, 0.28)

  const handleToggle = () => {
    if (collapsible) {
      setExpanded(!expanded)
    }
  }

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 3,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        overflow: 'hidden',
        transition: theme.transitions.create(['box-shadow', 'border-color'], {
          duration: theme.transitions.duration.shortest,
        }),
        bgcolor: 'background.paper',
        backgroundImage: 'none',
        boxShadow: theme.shadows[1],
        '&:hover': {
          boxShadow: theme.shadows[3],
          borderColor: primaryBorder,
        },
      }}
    >
      {/* Section Header */}
      <Box
        sx={{
          p: 3,
          pb: collapsible ? 2 : 3,
          cursor: collapsible ? 'pointer' : 'default',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'background.paper',
          backgroundImage: 'none',
          borderBottom:
            collapsible && expanded ? `1px solid ${theme.palette.divider}` : 'none',
          transition: theme.transitions.create('background-color', {
            duration: theme.transitions.duration.shortest,
          }),
          '&:hover': {
            bgcolor: primarySoft,
          },
        }}
        onClick={handleToggle}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          {icon && (
            <Box
              sx={{
                mr: 2,
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {icon}
            </Box>
          )}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                mb: description ? 0.5 : 0,
                fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
              }}
            >
              {title}
            </Typography>
            {description && (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.6,
                  fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                }}
              >
                {description}
              </Typography>
            )}
          </Box>
        </Box>

        {collapsible && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              handleToggle()
            }}
            sx={{
              color: 'text.secondary',
              transition: theme.transitions.create(['transform', 'color', 'background-color'], {
                duration: theme.transitions.duration.shortest,
              }),
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              '&:hover': {
                bgcolor: alpha(primary, 0.12),
                color: 'primary.main',
              },
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        )}
      </Box>

      {/* Section Content */}
      <Collapse in={!collapsible || expanded}>
        <Box
          sx={{
            p: 3,
            pt: collapsible ? 2 : 3,
            bgcolor: 'background.paper',
            backgroundImage: 'none',
          }}
        >
          {children}
        </Box>
      </Collapse>
    </Paper>
  )
}

export default ConfigSection
